/* eslint-disable no-restricted-globals */
import { useDispatch, useSelector } from "react-redux";
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";

import Logo from "./Logo";
import { Logger } from "src/services";
import { AppConstant } from "src/latest/src";
import { useIsMobile } from "src/latest/src/hooks";
import Storage from "../../utils/Storage";

import {
    deepClone,
    handleQuestionRender,
    isEmptyObject,
    prepareResponseData,
    stringToObject,
} from "../../utils/helpers";
import { fetchUserInfoV1 } from "../../api/user";
import { authUser } from "../../redux/modules/authentication";
import { UserServiceSection } from "../../latest/src/constants/AppServerConfig";
import ClientOnboardingModal from "src/latest/src/containers/QuestionnaireParent/components/ClientOnboardingModal/ClientOnboardingModal";
import { evaluateSubmitButtonStatus } from "src/latest/src/utils/questionnaireSubmitButtonStatus";
import {
    fetchQuestionnaireMetaData,
    fetchQuestions,
    saveResponses,
    updateContinueButton,
    updateQuestionnaireConfig,
    setIsCompleted,
    setQuestionnaireData,
    updateSignPadStatus,
} from "../../redux/actions/questionnaireActions";
import QuestionnairePage from "../QuestionnaireParent/QuestionnairePage";
import AppButton from "./QuestionOptionsLibrary/components/Button/Button";
import "./questionnaire.scss";
import { QuestionnaireGroupType } from "src/containers/QuestionnaireParent/QuestionnaireEnum";
import { Typography } from "src/latest/src/components";

type Props = {
    fromModal?: Boolean,
    fromEditProfile?: Boolean,
    questionnaireData?: Array<Object>,
    onResponseSaved?: any,
    hideProgressBar?: boolean
};

export default function QuestionnaireParentContainer({
    fromModal = false,
    fromEditProfile = false,
    questionnaireData = null,
    onResponseSaved,
    hideProgressBar
}: Props): React$Element<any> {
    const [searchParams] = useSearchParams();
    const fetchQuestionnaire = searchParams.get('fetchQuestionnaire');
    const { poweredByI2ILogo, tenantBackgroundColor } = useSelector(
        (state) => state?.configuration
    );
    const {
        isCompleted,
        currentPage,
        currentQuestions,
        isContinueButtonDisabled,
        direction,
        currentCategory,
        header,
        progress: questionnaireProgress,
        loading: questionnaireLoading,
        questionnaireSessionId: sessionId,
    } = useSelector((state) => state?.questionnaireData?.currentConfig);
    const {
        show_match_score_btn: showMatchScoreBtn,
        enhanced_information: therapyInfo,
        questionnaire_group: questionnaireGroup,
    } = useSelector((state) => state?.questionnaireData?.initialMetadata);
    const { isAuthed, loggedInUser } = useSelector(
        (state) => state?.authentication
    );
    const history = useNavigate();
    const locationG = useLocation();
    const dispatch = useDispatch();
    const [locationKeys, setLocationKeys] = useState([]);
    const [isFileTypeInvalid, setIsFileTypeInvalid] = useState(false);
    const [buttonLabel, setButtonLabel] = useState(
        fromModal ? "Save Changes" : "Continue"
    );
    const [
        hasContinueButtonStatusEvaluatedBefore,
        setHasContinueButtonStatusEvaluatedBefore,
    ] = useState(false);
    const [hasNegativeResponse, setHasNegativeResponse] = useState(false);
    const [uploading, setUploading] = useState(false);
    const pageUniqueKey = locationG.state?.state?.pageKey;
    const isFromMobile = useIsMobile();
    const [pageMetadata, setPageMetadata] = useState({});
    const showLocationModal = useSelector((state) => state?.questionnaireData?.initialMetadata?.show_location_modal);
    const [clientOnboardingModalOnboardingVisibility, setClientOnboardingModalOnboardingVisibility] = useState(false);
    // const btnWhy = document.querySelector(".btn-why");
    // TODO: need to remove it later, as checkin checkout functionality is not there now
    // later when we start vedio calls, we can evaluate and remove it if not needed

   

    useEffect(async () => {

        Storage.removeItem("isCompleted");

        if (!fromModal) {
            const isOptOut = await redirectToOptOutPage();

            if (isOptOut) {
                history("/therapy/opt-out");
            } else if (fetchQuestionnaire) {
                await initData(pageUniqueKey);
            }
        }
    }, []);

    const redirectToOptOutPage = async () => {
        if (Storage.getItem("opt-out") !== "completed") {
            return loggedInUser?.metadata
                ? loggedInUser?.metadata["opt-out-enable"] === "true"
                : false;
        } else {
            return false;
        }
    };

    const hideGoBackButtonForPages = [
        questionnaireGroup?.type ===
        QuestionnaireGroupType.SELF_MATCHING_CLIENT_INTAKE && clientOnboardingModalOnboardingVisibility &&  "clinicialAvailabilitySelfMatchingClientPgV1"
    ];

    const shouldHideGoBackButton = !hideGoBackButtonForPages.includes(currentPage?.unique_key);

    const hideContinueButtonForPages = [
        "clientOnboardingBySchedulerWelcomePage",
        "selfInvitationSelfMatchingClientPgV1",
        "enhancedEsignForSelfMatchingClientAdultPgV1",
        "therapistOnboardingBySchedulerCompletionWelcomeScreenV1",
        "intakeCompletionLastPage",
        "schedulerIntakeCompletionWelcomeScreenV1",
        "PracticeAdminIntakeCompletionWelcomeScreenV1",
        !loggedInUser?.id && "clinicialAvailabilitySelfMatchingClientPgV1",
        "clientOnboardingWelcomePage",
        "intakeCompletionWelcomeScreen"
    ];

    const shouldHideContinueButton =
    hideContinueButtonForPages.includes(currentPage?.unique_key) ||
    (loggedInUser?.user_type?.includes(
        'THERAPIST',
      ) && currentPage?.unique_key?.includes("eSignConsentPagePerClinician"));
    
    // we're hiding comman quesionnaire continue button in all upload consent document pages because its not managable to handle the required conditions if we use comman continue button, so we have made another continue button for upload consent document component.

    // for modal questions - conditional rendering
    useEffect(() => {
        if (questionnaireData && isAuthed) initData();
    }, [questionnaireData]);

    useEffect(() => {
        if (location.action === "PUSH") {
            setLocationKeys([location.key]);
        } else if (location.action === "POP") {
            if (locationKeys[1] === location.key) {
                setLocationKeys(([_, ...keys]) => keys);
                // Handle forward event
                dispatch(updateQuestionnaireConfig());
            } else {
                setLocationKeys((keys) => [location.key, ...keys]);

                if (questionnaireGroup?.type?.includes("CHECKIN")) {
                    history("/therapy/client");
                }

                // Handle back event
                dispatch(updateQuestionnaireConfig(null, "backward"));
            }
        }
    }, [locationKeys]);

    const initData = async (pageUniqueKey) => {        
        if (!fromModal) {
            await dispatch(setIsCompleted(false));
            await dispatch(fetchQuestionnaireMetaData(true, pageUniqueKey));
        } else {
            try {
                await dispatch(setQuestionnaireData(questionnaireData));
            } catch (e) {
                Logger.log("Questionnaire Parent", e);
            }
        }
    };

    const handleOnclick = async (updatedChoices?: any) => {
        const choiceMetadata = currentQuestions[0]?.choices[0]?.metadata || {};

        if (
            currentPage.name.includes("esign") && // esign screen
            choiceMetadata.signed === "false" // not signed yet
        ) {
            dispatch(updateSignPadStatus(true)); // open signature pad
        } else {
            let questions = deepClone(currentQuestions);

            if (questions && questions.length !== 0) {
                questions = questions.map((question) => {
                    const shouldRender = handleQuestionRender(
                        question,
                        questions
                    );

                    if (!shouldRender) {
                        return updateHiddenQuestion(question);
                    } else {
                        return question;
                    }
                });

                const response = prepareResponseData(questions, updatedChoices);

                const hasChangedResponses = true;

                if (questionnaireGroup?.type === QuestionnaireGroupType.INDIVIDUAL_CLIENT_CHECKOUT) {
                    // return only the questions with negative feedback but except the first question
                    const negativeFeedbacks = questions.filter(
                        (q, index) =>
                            index !== 0 &&
                            q?.metadata?.question_type === "customslider" &&
                            q?.choices[0]?.value < -66
                    );

                    if (negativeFeedbacks.length >= 1)
                        setHasNegativeResponse(true);
                }

                dispatch(
                    saveResponses({
                        sessionId,
                        categoryId: currentCategory?.id,
                        pageId: currentPage?.id,
                        responses: response,
                        hasChangedResponses,
                        responseCallback,
                    })
                );
            }
        }
    };

    const responseCallback = () => {
        // if (fromModal) {
        if (onResponseSaved)
            onResponseSaved(currentQuestions);
        // }
    };

    useEffect(() => {
        setTimeout(() => {
            if (isCompleted === true && !fromModal) {
                if (questionnaireGroup?.type?.toLowerCase()?.includes("intake")) {
                    // call the user service to get updated data after questionnaire intake
                    // https://app.clickup.com/t/36077897/DEV-2775

                    fetchUserInfoV1(UserServiceSection.profile_display).then(
                        (myInfo) => {
                            const role = myInfo.user_type?.includes("THERAPIST")
                                ? "therapist"
                                : myInfo.user_type?.includes("SCHEDULER")
                                    ? "scheduler"
                                    : myInfo.user_type?.includes("PRACTICE_ADMIN")
                                        ? "practice_admin"
                                        : "client";

                            Storage.setItem("user_type", myInfo.user_type);
                            if (myInfo)
                                dispatch(
                                    authUser({ ...myInfo, userRole: role })
                                ); // update user's redux store
                        }
                    );
                }

                if (questionnaireGroup?.type === QuestionnaireGroupType.INDIVIDUAL_CLIENT_CHECKIN && fetchQuestionnaire ) {
                    history("/teletherapy/waiting-room");

                    return ; 
                } else if (
                    questionnaireGroup?.type === QuestionnaireGroupType.INDIVIDUAL_CLIENT_CHECKOUT &&
                    hasNegativeResponse &&
                    (localStorage.getItem("lastSessionId") || therapyInfo?.therapySessionId)
                ) {
                    history("/request-rematch", {
                        therapistName:
                            localStorage.getItem("lastTherapistName") ||
                            therapyInfo?.fullName ||
                            "last therapist", // there is no way
                        matchType: "INDIVIDUAL",
                        sessionId:
                            localStorage.getItem("lastSessionId") ||
                            therapyInfo?.therapySessionId,
                    });
                }

                if (questionnaireGroup?.type === QuestionnaireGroupType.SELF_MATCHING_CLIENT_INTAKE) {
                    if (pageMetadata.pre_onboarded_user_last_page === "true") {
                        history("/therapy/clinician-availability");
                    } else {
                        history("/meeting-info");

                    }
                } else {
                    Logger.log("Questionnaire Parent container (useEffect isCompleted): Redirecting to /");

                    // as the role wise dashboard will be determined by the main component
                    history("/");
                }
            }
        }, 100);
    }, [isCompleted]);

    /** fetch questions on page change **/
    useEffect(() => {
        const pageMetadata =
            currentPage?.metadata && stringToObject(currentPage?.metadata);

        setPageMetadata(pageMetadata);

        if (currentPage?.id && !fromModal)
            dispatch(fetchQuestions(currentPage.id, sessionId, direction));
    }, [currentPage?.id]);

    // update the value of the hidden questions
    const updateHiddenQuestion = (question: any) => {
        const hiddenQuestion = deepClone(question);

        // prepare the updated question
        hiddenQuestion.choices = hiddenQuestion.choices.map((choice) => {
            if (choice.selected || choice.value) {
                choice.value = null;
                choice.selected = false;
            }

            return choice;
        });

        return hiddenQuestion;
    };

    useEffect(() => {
        if (currentQuestions && currentQuestions.length !== 0) {
            const choiceMetadata = currentQuestions[0]?.choices[0]?.metadata || {};

            if (window.location.pathname.includes("/profile/")) {
                setButtonLabel("Save Changes");
            } else if (
                currentPage?.name?.includes("esign") && // esign screen
                choiceMetadata.signed === "false" // not signed yet
            ) {
                setButtonLabel("Sign");
            } else {
                setButtonLabel("Continue");
            }

            if (hasContinueButtonStatusEvaluatedBefore || !fromEditProfile) {
                const _continueButtonStatus = evaluateSubmitButtonStatus(
                    currentQuestions,
                    currentPage
                );

                dispatch(
                    updateContinueButton({
                        buttonStatus: _continueButtonStatus ? "enabled" : "disabled",
                    })
                );

            } else {
                setHasContinueButtonStatusEvaluatedBefore(true);
            }

            setClientOnboardingModalOnboardingVisibility(true);
        }
    }, [currentQuestions]);

    const goBack = () => {
        dispatch(updateQuestionnaireConfig(null, "backward"));

        if (
            questionnaireProgress?.currentPageNumber === 1 &&
            loggedInUser?.metadata &&
            (loggedInUser?.metadata["opt-out-enable"] === "true")
        ) {

            history("/therapy/opt-out"); // show optout page on back button click on first page
        }
    };

    return (
        <div
            style={{
                backgroundColor:
                    tenantBackgroundColor ||
                    AppConstant.defaultAppBackgroundColor,
            }}
            className={`questionnaire ${currentPage?.name === "intakeCompletionWelcomeScreen" &&
                "pad-zero"
                }`}
        >
            {header?.visible && !hideProgressBar && (
                <div className="survey-progress-base">
                    <div
                        className="survey-progress"
                        style={{ width: questionnaireProgress?.progress + "%" }}
                    />
                </div>
            )}
            <div className="d-flex justify-content-center align-items-center">
                <div
                    className="session-questionnaire-container"
                    style={{
                        marginBottom:
                            isFromMobile &&
                                showMatchScoreBtn &&
                                currentPage.unique_key ===
                                "clinicialAvailabilitySelfMatchingClientPgV1"
                                ? "48px"
                                : "0px",
                    }}
                >
                    <div className="session-questionnaire">
                        <QuestionnairePage
                            currentPage={currentPage}
                            fromEditProfile={fromEditProfile}
                            setUploading={setUploading}
                            isFileTypeInvalid={isFileTypeInvalid}
                            setIsFileTypeInvalid={setIsFileTypeInvalid}
                            handleOnclick={handleOnclick}
                        />
                        {(
                            <div className="submitContainer">
                                {/* Show this loaded when there is no page */}
                                {(!currentPage ||
                                    isEmptyObject(currentPage)) && (
                                        <div className="question-loader">
                                            <Spinner
                                                animation="border"
                                                role="status"
                                            />
                                        </div>
                                    )}
                                {shouldHideContinueButton
                                    ? ""
                                    : !isEmptyObject(currentPage) && (
                                        <AppButton
                                            id="questionnaire-continue-button"
                                            className="primary no-border"
                                            disabled={
                                                uploading ||
                                                isContinueButtonDisabled ||
                                                (isFileTypeInvalid && true)
                                            }
                                            onClick={() => {
                                                handleOnclick();
                                            }}
                                            label={buttonLabel}
                                            isProcessing={
                                                questionnaireLoading
                                            }
                                        />
                                    )}
                                   
                                {(questionnaireProgress?.currentPageNumber >
                                    1 ||
                                    (loggedInUser?.metadata &&
                                        loggedInUser?.metadata[
                                        "opt-out-enable"
                                        ] === "true"))  && shouldHideGoBackButton && (
                                        <div
                                            className="go-back-button"
                                            onClick={goBack}
                                        >
                                            <Typography
                                                color="color-26"
                                                variant="size-14"
                                                weight="bold"
                                            >
                                                <FontAwesomeIcon
                                                    icon={faArrowLeft}
                                                />{" "}
                                                Go Back
                                            </Typography>
                                        </div>
                                    )}

                                {questionnaireGroup?.type &&
                                    questionnaireGroup?.type ===
                                    QuestionnaireGroupType.SELF_MATCHING_CLIENT_INTAKE &&
                                    currentQuestions ? (
                                    <Logo />
                                ) : (
                                    <>
                                        {poweredByI2ILogo && (
                                            <div className="text-center powered-by-container">
                                                <div className="poweredby_logo">
                                                    <img
                                                        src={`${decodeURIComponent(
                                                            poweredByI2ILogo
                                                        )}`}
                                                        alt="logo"
                                                        className="d-inline-block"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {showLocationModal && clientOnboardingModalOnboardingVisibility &&
                questionnaireGroup?.type ===
                QuestionnaireGroupType.SELF_MATCHING_CLIENT_INTAKE && (
                    <ClientOnboardingModal />
                )}
        </div>
    );
}
